import React, { useState, useEffect } from 'react';
import {
  Flex, VStack, Text, Textarea,
  Grid, GridItem
} from '@chakra-ui/react';
import PageHeading from "../../components/PageHeading";
import CopyButton from "../../components/Button/Copy";

const TextAnalyzer = () => {
  const [text, setText] = useState('');
  const [stats, setStats] = useState({
    words: 0,
    characters: 0,
    charactersNoSpace: 0,
    syllables: 0,
    sentences: 0,
    paragraphs: 0,
    readingTime: 0,
    speakingTime: 0,
  });

  const countSyllables = (word) => {
    word = word.toLowerCase();
    if (word.length <= 3) return 1;

    word = word.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, '');
    word = word.replace(/^y/, '');
    const syllableCount = word.match(/[aeiouy]{1,2}/g);
    return syllableCount ? syllableCount.length : 1;
  };

  const analyzeText = (text) => {
    // Words
    const words = text.trim().split(/\s+/).filter(word => word.length > 0);
    const wordCount = words.length;

    // Characters
    const charCount = text.length;
    const charNoSpaceCount = text.replace(/\s+/g, '').length;

    // Syllables
    const syllableCount = words.reduce((acc, word) => acc + countSyllables(word), 0);

    // Sentences
    const sentenceCount = text.split(/[.!?]+/).filter(sentence => sentence.trim().length > 0).length;

    // Paragraphs
    const paragraphCount = text.split(/\n\s*\n/).filter(para => para.trim().length > 0).length;

    // Reading time (assuming 200 words per minute)
    const readingTime = Math.ceil(wordCount / 200);

    // Speaking time (assuming 130 words per minute)
    const speakingTime = Math.ceil(wordCount / 130);

    setStats({
      words: wordCount,
      characters: charCount,
      charactersNoSpace: charNoSpaceCount,
      syllables: syllableCount,
      sentences: sentenceCount,
      paragraphs: paragraphCount || 0,
      readingTime: readingTime || 0,
      speakingTime: speakingTime || 0,
    });
  };

  useEffect(() => {
    analyzeText(text);
    // eslint-disable-next-line
  }, [text]);

  const StatBox = ({ colSpan, label, value }) => (
    <GridItem
      p={2}
      textAlign="center"
      borderWidth="1px"
      borderRadius="md"
      colSpan={colSpan}
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
    >
      <VStack align="center" gap={0}>
        <Text fontSize="2xl" fontWeight="bold">{value}</Text>
        <Text color="gray.400" fontSize="sm">{label}</Text>
      </VStack>
    </GridItem>
  );

  return (
    <VStack spacing={6} align="stretch">
      <PageHeading
        mainText="Word Counter"
        subText="Free online word and character counter – fast, simple, and accurate"
      />
      <Flex direction={{ base: "column", md: "row" }} gap={8} justifyContent={"center"} mt={"12px"}>
        <VStack align="start" flex={1} minWidth={"45vw"}>
          <Flex gap={1} justifyContent={"flex-end"} alignItems={"center"} width={"100%"}>
            <CopyButton text={text} />
          </Flex>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            height="100%"
            minHeight={"60vh"}
            maxHeight={"80vh"}
          />
        </VStack>

        <Grid
          templateColumns={{
            base: "repeat(2, 1fr)",
            md: "repeat(12, 1fr)"
          }}
          gap={1}
          borderRadius="lg"
        >
          <StatBox colSpan={12} label="Words" value={stats.words} />
          <StatBox colSpan={6} label="Characters" value={stats.characters} />
          <StatBox colSpan={6} label="Characters without space" value={stats.charactersNoSpace} />
          <StatBox colSpan={4} label="Syllables" value={stats.syllables} />
          <StatBox colSpan={4} label="Sentences" value={stats.sentences} />
          <StatBox colSpan={4} label="Paragraphs" value={stats.paragraphs} />
          <StatBox colSpan={6} label="Reading time (min)" value={stats.readingTime} />
          <StatBox colSpan={6} label="Speaking time (min)" value={stats.speakingTime} />
        </Grid>
      </Flex>
    </VStack>
  );
};

export default TextAnalyzer;